// Cognito email/password auth (SRP) via amazon-cognito-identity-js.
// Invite-only: no sign-up. Handles the NEW_PASSWORD_REQUIRED challenge that
// Cognito issues on first sign-in with an admin-created temporary password, and
// the forgot-password / admin-reset flow (code by email, then a new password).
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

let pool: CognitoUserPool | null = null;

function getPool(): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
    });
  }
  return pool;
}

export type SignInResult =
  | { status: "success" }
  | { status: "new_password_required" }
  // An administrator reset the password (Cognito RESET_REQUIRED): the user must
  // finish with the emailed code via confirmForgotPassword().
  | { status: "reset_required" };

export type Claims = {
  sub: string;
  email: string;
  roles: string[];
};

// User held between signIn() returning "new_password_required" and
// completeNewPassword() finishing the challenge.
let pendingChallengeUser: CognitoUser | null = null;

export function signIn(email: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: () => {
        pendingChallengeUser = null;
        resolve({ status: "success" });
      },
      onFailure: (err) => {
        pendingChallengeUser = null;
        if (errorCode(err) === "PasswordResetRequiredException") {
          resolve({ status: "reset_required" });
          return;
        }
        reject(toError(err));
      },
      newPasswordRequired: () => {
        pendingChallengeUser = user;
        resolve({ status: "new_password_required" });
      },
    });
  });
}

export function completeNewPassword(newPassword: string): Promise<SignInResult> {
  const user = pendingChallengeUser;
  if (!user) {
    return Promise.reject(new Error("No pending sign-in. Please sign in again."));
  }
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: () => {
          pendingChallengeUser = null;
          resolve({ status: "success" });
        },
        onFailure: (err) => {
          reject(toError(err));
        },
      },
    );
  });
}

// Emails a verification code to the user (Cognito ForgotPassword). Cognito's
// own message is used unless the pool's custom-message trigger replaces it.
export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toError(err)),
    });
  });
}

// Sets a new password with the emailed code (Cognito ConfirmForgotPassword).
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: getPool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toError(err)),
    });
  });
}

/** Cognito error code (e.g. "CodeMismatchException"), when there is one. */
export function errorCode(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const e = err as { code?: unknown; name?: unknown };
  if (typeof e.code === "string" && e.code) return e.code;
  if (typeof e.name === "string" && e.name && e.name !== "Error") return e.name;
  return undefined;
}

function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  const e = err as { message?: unknown } | null | undefined;
  const out = new Error(String(e?.message ?? err)) as Error & { code?: string };
  const code = errorCode(err);
  if (code) out.code = code;
  return out;
}

// Current valid session, or null. The library refreshes expired tokens
// automatically when a refresh token is available.
export function getSession(): Promise<CognitoUserSession | null> {
  const user = getPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) resolve(null);
      else resolve(session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

export async function getClaims(): Promise<Claims | null> {
  const session = await getSession();
  if (!session) return null;
  const payload = session.getIdToken().payload as Record<string, unknown>;
  const rolesRaw = typeof payload["custom:roles"] === "string" ? (payload["custom:roles"] as string) : "";
  return {
    sub: (payload.sub as string) ?? "",
    email: (payload.email as string) ?? "",
    roles: rolesRaw
      .split(",")
      .map((r) => r.trim())
      .filter(Boolean),
  };
}

export function clearSession(): void {
  pendingChallengeUser = null;
  getPool().getCurrentUser()?.signOut();
}

export function signOut(): void {
  clearSession();
}
